import { useEffect, useRef, useState } from "react";
import { ShieldCheck, Zap } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";

export default function TestLogin() {
  const [mode, setMode] = useState("single");
  const [single, setSingle] = useState("");
  const [boxes, setBoxes] = useState(["", "", "", "", "", ""]);
  const [submitted, setSubmitted] = useState("");
  const [busy, setBusy] = useState(false);
  const refs = useRef([]);

  useEffect(() => { document.title = "Passcode Hub · Test sign-in"; }, []);

  const sendTest = async () => {
    setBusy(true);
    try { const r = await api.post("/codes/test", {}); toast.success(`Test code ${r.data.code} is on its way — the prompt should appear in a few seconds.`); }
    catch (e) { toast.error(e?.response?.status === 401 ? "Sign in to the hub (as admin) in another tab first." : errMsg(e)); }
    finally { setBusy(false); }
  };

  const submit = (e) => {
    e.preventDefault();
    setSubmitted(mode === "single" ? single : boxes.join(""));
  };

  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <div className="rise w-full max-w-md">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-emerald-500 text-emerald-950"><ShieldCheck className="h-4 w-4" /></span>
            <div>
              <h1 className="font-display text-xl font-bold text-zinc-100">Test sign-in</h1>
              <p className="text-xs text-zinc-500">A fake login page to try the extension on</p>
            </div>
          </div>
          <Button variant="secondary" size="sm" onClick={sendTest} disabled={busy} className="gap-2" data-testid="test-send-code-button"><Zap className="h-3.5 w-3.5" /> Send test code</Button>
        </div>

        <form onSubmit={submit} className="surface flex flex-col gap-4 p-6" data-testid="test-login-form">
          <div className="grid grid-cols-2 gap-3">
            <Input value="shared.user@yourco.com" readOnly className="bg-[#0B0D11] text-zinc-400" aria-label="Username" />
            <Input value="••••••••••" type="password" readOnly className="bg-[#0B0D11] text-zinc-400" aria-label="Password" />
          </div>
          <div className="flex items-center justify-between">
            <p className="text-sm text-zinc-300">Enter the 6-digit verification code we sent you</p>
            <button type="button" onClick={() => setMode(mode === "single" ? "split" : "single")} className="text-xs text-zinc-500 hover:text-zinc-300" data-testid="test-toggle-mode">
              {mode === "single" ? "Use split boxes" : "Use single field"}
            </button>
          </div>
          {mode === "single" ? (
            <input
              id="verification-code" name="verificationCode" autoComplete="one-time-code" inputMode="numeric" maxLength={8}
              value={single} onChange={(e) => setSingle(e.target.value)} placeholder="Verification code"
              className="code-digits h-14 w-full rounded-md border border-[#242A36] bg-[#0B0D11] px-4 text-2xl outline-none focus:border-emerald-500"
              data-testid="test-code-input"
            />
          ) : (
            <div className="flex gap-2" aria-label="Verification code">
              {boxes.map((v, i) => (
                <input
                  key={i} ref={(el) => { refs.current[i] = el; }} value={v} maxLength={1} inputMode="numeric" aria-label={`Digit ${i + 1}`}
                  onChange={(e) => { const n = [...boxes]; n[i] = e.target.value.slice(-1); setBoxes(n); if (e.target.value && refs.current[i + 1]) refs.current[i + 1].focus(); }}
                  className="code-digits h-14 w-full rounded-md border border-[#242A36] bg-[#0B0D11] text-center text-2xl outline-none focus:border-emerald-500"
                  data-testid={`test-code-box-${i}`}
                />
              ))}
            </div>
          )}
          <Button type="submit" className="h-11 font-semibold" data-testid="test-submit-button">Verify</Button>
          {submitted && <p className="text-sm text-emerald-300" data-testid="test-submitted-value">Code entered: <span className="font-mono font-bold">{submitted}</span></p>}
        </form>
        <p className="mt-4 text-center text-xs text-zinc-600">Install the extension and sign in from its popup. The Fill prompt appears beside the field when a fresh code exists.</p>
      </div>
    </div>
  );
}
